<template>
  <el-container class="layout-container">
    <!-- 侧边栏 -->
    <el-aside :width="isCollapse ? '64px' : '200px'" class="sidebar">
      <div class="logo">
        <span v-if="!isCollapse">运营商后台</span>
        <el-icon v-else><Shop /></el-icon>
      </div>

      <el-menu
        :default-active="activeMenu"
        :collapse="isCollapse"
        :collapse-transition="false"
        router
        background-color="#304156"
        text-color="#bfcbd9"
        active-text-color="#409EFF"
      >
        <sidebar-item
          v-for="route in menuList"
          :key="route.path"
          :item="route"
        />
      </el-menu>
    </el-aside>

    <el-container>
      <!-- 顶部导航 -->
      <el-header class="header">
        <div class="header-left">
          <el-icon class="collapse-btn" @click="toggleCollapse">
            <Fold v-if="!isCollapse" />
            <Expand v-else />
          </el-icon>
          <breadcrumb />
          <div v-if="hasPending" class="finance-pending-bar" role="status">
            <el-icon class="finance-pending-bar__icon"><BellFilled /></el-icon>
            <span class="finance-pending-bar__text">
              待审核：上分 <strong>{{ topupPending }}</strong> 笔 · 下分 <strong>{{ withdrawPending }}</strong> 笔
              <template v-if="financeNotifyMuted">（已静音）</template>
              <template v-else>（提示音循环播报中，全部处理完后自动停止）</template>
            </span>
            <el-button type="warning" link size="small" @click="financePendingStore.setMuted(!financeNotifyMuted)">
              {{ financeNotifyMuted ? '开启提示音' : '静音本次会话' }}
            </el-button>
          </div>
        </div>

        <div class="header-right">
          <el-dropdown @command="handleCommand">
            <span class="user-info">
              {{ userInfo.username }}
              <el-icon><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="profile">个人中心</el-dropdown-item>
                <el-dropdown-item command="password">修改密码</el-dropdown-item>
                <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </el-header>

      <!-- 主内容区 -->
      <el-main class="main-content">
        <router-view />
      </el-main>
    </el-container>
  </el-container>
</template>

<script>
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import SidebarItem from './SidebarItem.vue'
import Breadcrumb from './Breadcrumb.vue'
import { logout } from '@/api/auth'
import { usePermissionStore } from '@/stores/permission'
import { useCsNotifyStore } from '@/stores/cs-notify'
import { useFinancePendingNotifyStore } from '@/stores/finance-pending-notify'
import { storeToRefs } from 'pinia'

export default {
  name: 'Layout',
  components: {
    SidebarItem,
    Breadcrumb
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const permissionStore = usePermissionStore()
    const csNotifyStore = useCsNotifyStore()
    const financePendingStore = useFinancePendingNotifyStore()
    const { hasPending, topupPending, withdrawPending, muted: financeNotifyMuted } = storeToRefs(financePendingStore)
    const isCollapse = ref(false)

    const userInfo = computed(() => {
      return permissionStore.user || {}
    })

    const menuList = computed(() => {
      return (permissionStore.menuTree || []).filter(item => !item.hidden)
    })

    // 当前激活的菜单
    const activeMenu = computed(() => {
      return route.path
    })

    // 切换侧边栏
    const toggleCollapse = () => {
      isCollapse.value = !isCollapse.value
    }

    // 处理下拉菜单命令
    const handleCommand = (command) => {
      switch (command) {
        case 'profile':
          router.push('/profile')
          break
        case 'password':
          router.push('/password')
          break
        case 'logout':
          handleLogout()
          break
      }
    }

    // 退出登录
    const handleLogout = () => {
      ElMessageBox.confirm('确认退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await logout()
        } catch (error) {
          console.error('退出登录失败:', error)
        } finally {
          csNotifyStore.teardown()
          financePendingStore.teardown()
          localStorage.removeItem('token')
          localStorage.removeItem('userInfo')
          permissionStore.reset()
          router.push('/login')
        }
      }).catch(() => {})
    }

    return {
      isCollapse,
      menuList,
      userInfo,
      activeMenu,
      toggleCollapse,
      handleCommand,
      financePendingStore,
      hasPending,
      topupPending,
      withdrawPending,
      financeNotifyMuted
    }
  }
}
</script>

<style lang="scss" scoped>
.layout-container {
  height: 100vh;

  .sidebar {
    background-color: #304156;
    transition: width 0.3s;

    .logo {
      height: 60px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-size: 18px;
      font-weight: bold;
      border-bottom: 1px solid #1f2d3d;
    }

    .el-menu {
      border-right: none;
    }
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: #fff;
    box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);

    .header-left {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px 12px;
      min-width: 0;
      flex: 1;

      .collapse-btn {
        font-size: 20px;
        cursor: pointer;
        margin-right: 15px;

        &:hover {
          color: #409EFF;
        }
      }

      .finance-pending-bar {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
        margin-left: 8px;
        padding: 6px 12px;
        border-radius: 8px;
        background: linear-gradient(90deg, #fff7e6 0%, #ffe7ba 100%);
        border: 1px solid #faad14;
        font-size: 13px;
        color: #ad6800;
        max-width: min(720px, 100%);

        &__icon {
          font-size: 16px;
          color: #d48806;
          flex-shrink: 0;
        }

        &__text {
          flex: 1;
          min-width: 200px;
          line-height: 1.4;
        }
      }
    }

    .header-right {
      .user-info {
        cursor: pointer;
        display: flex;
        align-items: center;

        .el-icon {
          margin-left: 5px;
        }
      }
    }
  }

  .main-content {
    background-color: #f0f2f5;
    padding: 20px;
    overflow-y: auto;
  }
}
</style>
